import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  creditPackagesService,
  CreditPackageCreateData,
  CreditPackageUpdateData,
  CreditPackageFilters,
  CreditPackageSortOptions
} from '@/services/billing/credit-packages.service';
import { toast } from 'sonner';
import { CreditPackage, TranslatedCreditPackage, TranslationParams } from '@/types/billing';

// Helper function for error handling
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error && 
      typeof error === 'object' && 
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object') {
    
    // Check for JSON:API error format
    const data = error.response.data as Record<string, unknown>;
    if ('errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
      const firstError = data.errors[0] as Record<string, unknown>;
      if ('detail' in firstError && typeof firstError.detail === 'string') {
        return firstError.detail;
      }
    }
    
    // Fallback to message field
    if ('message' in data && typeof data.message === 'string') {
      return data.message;
    }
  }
  return defaultMessage;
}

// Query Keys
export const creditPackageKeys = {
  all: ['credit-packages'] as const,
  lists: () => [...creditPackageKeys.all, 'list'] as const,
  list: (productId: string, filters?: CreditPackageFilters, sort?: CreditPackageSortOptions, translationParams?: TranslationParams) => 
    [...creditPackageKeys.lists(), productId, filters, sort, translationParams] as const,
  details: () => [...creditPackageKeys.all, 'detail'] as const,
  detail: (productId: string, packageId: string, translationParams?: TranslationParams) => 
    [...creditPackageKeys.details(), productId, packageId, translationParams] as const,
};

/**
 * Hook to fetch all credit packages for a product
 */
export function useCreditPackages(
  productId: string,
  filters?: CreditPackageFilters,
  sort?: CreditPackageSortOptions,
  translationParams?: TranslationParams
) {
  return useQuery<TranslatedCreditPackage[]>({
    queryKey: creditPackageKeys.list(productId, filters, sort, translationParams),
    queryFn: () => creditPackagesService.getCreditPackages(productId, filters, sort, translationParams),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single credit package
 */
export function useCreditPackage(
  productId: string, 
  packageId: string,
  translationParams?: TranslationParams
) {
  return useQuery<TranslatedCreditPackage>({
    queryKey: creditPackageKeys.detail(productId, packageId, translationParams),
    queryFn: () => creditPackagesService.getCreditPackage(productId, packageId, translationParams),
    enabled: !!productId && !!packageId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new credit package
 */
export function useCreateCreditPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: CreditPackageCreateData }) =>
      creditPackagesService.createCreditPackage(productId, data),
    onSuccess: (newPackage, { productId }) => {
      // Set the detail cache for the new package
      queryClient.setQueryData(
        creditPackageKeys.detail(productId, newPackage.id),
        newPackage
      );
      
      // Invalidate all related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: creditPackageKeys.all });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'lists'] });
      
      // Specifically invalidate this product's lists with any filters/sorts
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey.includes('credit-packages') && 
                 queryKey.includes('list') &&
                 queryKey.includes(productId);
        }
      });
      
      toast.success('Credit package created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create credit package'));
    },
  });
}

/**
 * Hook to update a credit package (full update)
 */
export function useUpdateCreditPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      packageId, 
      data 
    }: { 
      productId: string; 
      packageId: string; 
      data: CreditPackageUpdateData 
    }) =>
      creditPackagesService.updateCreditPackage(productId, packageId, data),
    onSuccess: (updatedPackage, { productId, packageId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        creditPackageKeys.detail(productId, packageId),
        updatedPackage
      );
      
      // Invalidate all credit package queries
      queryClient.invalidateQueries({ queryKey: creditPackageKeys.all });
      
      // Invalidate all list queries for this product specifically
      queryClient.invalidateQueries({ queryKey: creditPackageKeys.lists() });
      
      // Also invalidate with exact match for the most common list query pattern
      queryClient.invalidateQueries({ 
        queryKey: creditPackageKeys.list(productId, {}, { field: 'credits' })
      });
      
      // Broader invalidation using predicate to catch any variations
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey[0] === 'credit-packages' && 
                 queryKey.includes(productId);
        }
      });
      
      toast.success('Credit package updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update credit package'));
    },
  });
}

/**
 * Hook to partially update a credit package
 */
export function usePatchCreditPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      packageId, 
      data 
    }: { 
      productId: string; 
      packageId: string; 
      data: Partial<CreditPackageUpdateData> 
    }) =>
      creditPackagesService.patchCreditPackage(productId, packageId, data),
    onSuccess: (updatedPackage, { productId, packageId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        creditPackageKeys.detail(productId, packageId),
        updatedPackage
      );
      
      // Invalidate all related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: creditPackageKeys.all });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'lists'] });
      
      // Specifically invalidate this product's lists with any filters/sorts
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey.includes('credit-packages') && 
                 queryKey.includes('list') &&
                 queryKey.includes(productId);
        }
      });
      
      toast.success('Credit package updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update credit package'));
    },
  });
}

/**
 * Hook to delete a credit package
 */
export function useDeleteCreditPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, packageId }: { productId: string; packageId: string }) =>
      creditPackagesService.deleteCreditPackage(productId, packageId),
    onSuccess: (_, { productId, packageId }) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: creditPackageKeys.detail(productId, packageId) });
      
      // Invalidate all related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: creditPackageKeys.all });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'lists'] });
      
      // Specifically invalidate this product's lists with any filters/sorts
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey.includes('credit-packages') && 
                 queryKey.includes('list') &&
                 queryKey.includes(productId);
        }
      });
      
      toast.success('Credit package deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete credit package. Please archive it instead if there are active purchases.'));
    },
  });
}

/**
 * Hook to archive a credit package
 */
export function useArchiveCreditPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, packageId }: { productId: string; packageId: string }) =>
      creditPackagesService.archiveCreditPackage(productId, packageId),
    onSuccess: (updatedPackage, { productId, packageId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        creditPackageKeys.detail(productId, packageId),
        updatedPackage
      );
      
      // Invalidate all related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: creditPackageKeys.all });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['credit-packages', 'lists'] });
      
      // Specifically invalidate this product's lists with any filters/sorts
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 queryKey.includes('credit-packages') && 
                 queryKey.includes('list') &&
                 queryKey.includes(productId);
        }
      });
      
      toast.success('Credit package archived successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to archive credit package'));
    },
  });
}

/**
 * Hook to calculate credit package savings
 */
export function useCreditPackageCalculation(packages: CreditPackage[], currency: string) {
  const bestValue = creditPackagesService.findBestValuePackage(packages, currency);
  const smallest = creditPackagesService.findSmallestPackage(packages);
  
  const calculateSavings = (pkg: CreditPackage) => {
    if (!smallest || pkg.id === smallest.id) return null;
    
    const smallestPricePerCredit = smallest.pricePerCredit?.[currency] || (smallest.prices[currency] / smallest.credits);
    const currentPricePerCredit = pkg.pricePerCredit?.[currency] || (pkg.prices[currency] / pkg.credits);
    
    return creditPackagesService.calculateSavings(
      currentPricePerCredit,
      smallestPricePerCredit,
      pkg.credits,
      currency
    );
  };
  
  return {
    bestValue,
    smallest,
    calculateSavings
  };
}

/**
 * Prefetch a credit package for hover/preview scenarios
 */
export function usePrefetchCreditPackage() {
  const queryClient = useQueryClient();

  return (productId: string, packageId: string) => {
    queryClient.prefetchQuery({
      queryKey: creditPackageKeys.detail(productId, packageId),
      queryFn: () => creditPackagesService.getCreditPackage(productId, packageId),
      staleTime: 5 * 60 * 1000,
    });
  };
}